import type { SvgIconComponent } from '@mui/icons-material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';

import type { LineId } from '../../permissions/permissionTypes';
import type { CatalogueLevel, CatalogueModule } from '../types/rights-profile.types';

export interface LineLabel {
  /**
   * i18n key (`rightsProfiles.lines.<lineId>`); FR/EN strings live in the locale JSON.
   * LineIds contain dots (`user_menu.help`), the i18next key separator — so the
   * segment replaces `.` with `_` (`lines.user_menu_help`) for a clean nested lookup.
   */
  i18nKey: string;
  /** Icon — PLACEHOLDER, to validate with design (HOMCO-326). */
  icon: SvgIconComponent;
}

/** i18n key of a line label — dots in the LineId replaced by `_` (i18next separator). */
export const LINE_LABEL_KEY = (line: LineId): string => `lines.${line.replace(/\./g, '_')}`;

/** i18n key of a module header — `rightsProfiles.modules.<module>`. */
export const MODULE_LABEL_KEY = (module: CatalogueModule): string => `modules.${module}`;

/** i18n key of a level prefix (« Interface / Fiche / Onglet… ») — `rightsProfiles.levels.<level>`. */
export const LEVEL_LABEL_KEY = (level: CatalogueLevel): string => `levels.${level}`;

/**
 * How each catalogue line is presented in the matrix: `LineId` → `{ i18nKey, icon }`.
 * Labels are resolved via i18next (fr/en) at render; icons are placeholders pending
 * design validation. Typed as `Record<LineId, …>` so the compiler flags any line
 * missing or extra — keeping this table 1:1 with the backend catalogue.
 */
export const LINE_LABELS: Record<LineId, LineLabel> = {
  user_menu: { i18nKey: LINE_LABEL_KEY('user_menu'), icon: AccountCircleOutlinedIcon },
  'user_menu.help': { i18nKey: LINE_LABEL_KEY('user_menu.help'), icon: HelpOutlineOutlinedIcon },
  employee_list: { i18nKey: LINE_LABEL_KEY('employee_list'), icon: GroupsOutlinedIcon },
  employee_record: { i18nKey: LINE_LABEL_KEY('employee_record'), icon: BadgeOutlinedIcon },
  'employee_record.identity': {
    i18nKey: LINE_LABEL_KEY('employee_record.identity'),
    icon: PersonOutlineOutlinedIcon,
  },
  'employee_record.contract': {
    i18nKey: LINE_LABEL_KEY('employee_record.contract'),
    icon: ArticleOutlinedIcon,
  },
  'employee_record.skills': {
    i18nKey: LINE_LABEL_KEY('employee_record.skills'),
    icon: PsychologyOutlinedIcon,
  },
  'employee_record.career_training': {
    i18nKey: LINE_LABEL_KEY('employee_record.career_training'),
    icon: SchoolOutlinedIcon,
  },
  'employee_record.evaluation': {
    i18nKey: LINE_LABEL_KEY('employee_record.evaluation'),
    icon: FactCheckOutlinedIcon,
  },
  'employee_record.leaves_absences': {
    i18nKey: LINE_LABEL_KEY('employee_record.leaves_absences'),
    icon: BeachAccessOutlinedIcon,
  },
  'employee_record.payment': {
    i18nKey: LINE_LABEL_KEY('employee_record.payment'),
    icon: PaymentsOutlinedIcon,
  },
  leave_list: { i18nKey: LINE_LABEL_KEY('leave_list'), icon: EventBusyOutlinedIcon },
  payslip_list: { i18nKey: LINE_LABEL_KEY('payslip_list'), icon: ReceiptLongOutlinedIcon },
  general_settings: { i18nKey: LINE_LABEL_KEY('general_settings'), icon: SettingsOutlinedIcon },
  rights_profiles: {
    i18nKey: LINE_LABEL_KEY('rights_profiles'),
    icon: AdminPanelSettingsOutlinedIcon,
  },
  users: { i18nKey: LINE_LABEL_KEY('users'), icon: PeopleOutlinedIcon },
  instances: { i18nKey: LINE_LABEL_KEY('instances'), icon: DnsOutlinedIcon },
};
